import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import logo from '../../assets/FinalLOGO.png';
import {
  DollarSign,
  CreditCard,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  FileText,
  TrendingUp,
  Wallet
} from 'lucide-react';
// import { CardSkeleton } from '../../Components/common/Skeleton';
import { Link } from 'react-router-dom';

const AccountantDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    pendingPayouts: 0,
    totalDisbursed: 0,
    activeSurgeries: 0,
    systemPayments: 0
  });
  const [recentPayouts, setRecentPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, payoutsRes] = await Promise.all([
        api.get('/dashboard'),
        api.get('/payouts?limit=5&status=completed')
      ]);

      setStats(dashboardRes.data.data.stats);
      setRecentPayouts(payoutsRes.data.data.payouts || []);
    } catch (error) {
      console.error('Failed to load accountant data:', error);
      // Fallback for missing stats endpoint
      setStats({
        pendingPayouts: 12,
        totalDisbursed: 450000,
        activeSurgeries: 8,
        systemPayments: 24
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, trend, trendValue, color }) => (
    <div className="bg-white rounded-[2rem] p-8 shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col justify-between">
      <div className="flex justify-between items-start">
        <div className={`p-4 rounded-2xl ${color} text-white`}>
          {icon}
        </div>
        <div className={`flex items-center gap-1 text-xs font-black ${trend === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
          {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trendValue}
        </div>
      </div>
      <div className="mt-6">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{title}</p>
        <p className="text-3xl font-black text-slate-800 mt-1">{value}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8 space-y-10 animate-fadeIn">
        <div className="flex items-center gap-6 mb-10">
          <img src={logo} alt="Loading..." className="h-20 w-auto opacity-50 grayscale animate-pulse" />
          <div className="h-20 flex-1 bg-white rounded-3xl animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-44 bg-white rounded-[2rem] animate-pulse" />)}
        </div>
        <div className="h-96 bg-white rounded-[2rem] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-10 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse" />
            <span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em]">Financial Intelligence</span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight capitalize">
            {user?.roleDetails?.role_name || user?.role?.replace(/_/g, ' ') || 'Accountant'} Command Center
          </h1>
          <p className="text-slate-500 font-medium mt-1">Reviewing fiscal health and managing partner disbursements.</p>
        </div>
        <div className="flex items-center gap-4 bg-white p-2 rounded-3xl shadow-lg shadow-slate-200/50 border border-slate-100 pr-6">
          <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white font-black text-lg">
            {user?.firstName?.charAt(0)}
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Logged in as</p>
            <p className="text-sm font-black text-slate-800 mt-1">{user?.firstName} {user?.lastName}</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <StatCard
          title="Awaiting Payouts"
          value={stats.pendingPayouts}
          icon={<Clock className="w-6 h-6" />}
          trend="up"
          trendValue="+12%"
          color="bg-orange-500"
        />
        <StatCard
          title="Total Disbursed"
          value={stats.totalDisbursed === 0 ? '₹0' : (stats.totalDisbursed >= 1000 ? `₹${(stats.totalDisbursed / 1000).toFixed(1).replace('.0', '')}k` : `₹${stats.totalDisbursed}`)}
          icon={<Wallet className="w-6 h-6" />}
          trend="up"
          trendValue="+8.4%"
          color="bg-emerald-500"
        />
        <StatCard
          title="Active Cases"
          value={stats.activeSurgeries}
          icon={<Activity className="w-6 h-6" />}
          trend="down"
          trendValue="-2%"
          color="bg-blue-600"
        />
        <StatCard
          title="Inbound Revenue"
          value={stats.systemPayments}
          icon={<TrendingUp className="w-6 h-6" />}
          trend="up"
          trendValue="+15%"
          color="bg-indigo-600"
        />
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Payouts Table */}
        <div className="lg:col-span-2 bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/40 border border-slate-100 overflow-hidden">
          <div className="p-8 border-b border-slate-50 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-black text-slate-800">Recent Disbursements</h2>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Finalized Payouts Ledger</p>
            </div>
            <Link
              to="/payouts"
              className="px-6 py-3 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all"
            >
              View Full Audit
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase">Beneficiary</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase">Amount</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase text-center">Status</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase text-right">Verification</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {recentPayouts.map((payout, idx) => (
                  <tr key={payout._id} className="hover:bg-indigo-50/30 transition-colors">
                    <td className="px-8 py-6">
                      <div className="font-black text-slate-800">
                        {payout.doctor?.user
                          ? `Dr. ${payout.doctor.user.firstName} ${payout.doctor.user.lastName || ''}`.trim()
                          : (payout.hospital?.hospitalName || 'Stakeholder')}
                      </div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                        {(payout.payoutType || 'both').toUpperCase()} • {payout.surgery?.bookingId || 'N/A'}
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className="font-black text-slate-900 font-mono">₹{payout.amount?.toLocaleString()}</span>
                    </td>
                    <td className="px-8 py-6 text-center">
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[9px] font-black uppercase tracking-widest">
                        {payout.status || 'Settled'}
                      </span>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <a
                        href={payout.documentUrl || payout.documents?.[0]?.url || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 bg-slate-100 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all inline-block"
                      >
                        <FileText className="w-4 h-4" />
                      </a>
                    </td>
                  </tr>
                ))}
                {recentPayouts.length === 0 && (
                  <tr>
                    <td colSpan="4" className="py-20 text-center font-bold text-slate-400">
                      No disbursed records found in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Sidebar */}
        <div className="space-y-8">
          {/* Quick Action Card */}
          <div className="p-8 bg-indigo-600 rounded-[2.5rem] text-white shadow-2xl shadow-indigo-200">
            <div className="p-4 bg-white/10 rounded-2xl w-max mb-6">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-black leading-tight italic">Unprocessed Settlements</h3>
            <p className="text-indigo-100 text-sm mt-2 font-medium opacity-80">There are {stats.pendingPayouts} cases ready for financial dispatch.</p>
            <Link
              to="/payouts/action-center"
              className="mt-8 w-full py-4 bg-white text-indigo-600 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:scale-[1.02] active:scale-95 transition-all text-center block"
            >
              Enter Payout Portal
            </Link>
          </div>

          {/* Quick Links Overlay */}
          <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 px-1">Navigation Protocols</h4>
            <div className="space-y-4">
              {[
                { label: 'Revenue Streams', path: '/payments', icon: CreditCard },
                { label: 'Payout History', path: '/payouts', icon: FileText },
                { label: 'Settlement Ledger', path: '/payouts/action-center', icon: Wallet }
              ].map((link, i) => (
                <Link
                  key={i}
                  to={link.path}
                  className="flex items-center justify-between p-5 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <link.icon className="w-4 h-4 text-slate-500 group-hover:text-white" />
                    <span className="text-xs font-black uppercase tracking-widest">{link.label}</span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountantDashboard;